import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { colors as C, fonts as F } from '../theme'
import { useIsMobile } from '../hooks/useIsMobile'

export default function CguPage() {
  const isMobile = useIsMobile()
  return (
    <div style={{ background: C.bgPage, minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: C.bgCard,
        borderBottom: `0.5px solid ${C.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: C.textPrimary,
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}>
            <ChevronLeft size={20} />
            Retour
          </Link>
          <div style={{
            fontSize: '1.2rem',
            fontFamily: F.serif,
            fontWeight: 400,
            color: C.textPrimary,
          }}>
            IAvocat<span style={{ fontStyle: 'italic', color: C.accent }}>.ai</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div style={{
        maxWidth: '760px',
        margin: '0 auto',
        padding: isMobile ? '32px 18px' : '60px 24px',
        background: C.bgCard,
        minHeight: '100vh',
      }}>
        <h1 style={{
          fontFamily: F.serif,
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: 400,
          color: C.textPrimary,
          marginBottom: '0.5rem',
        }}>
          Conditions Générales d'Utilisation
        </h1>
        <p style={{
          fontFamily: F.sans,
          fontSize: '12px',
          fontStyle: 'italic',
          color: C.textSecondary,
          marginBottom: '2rem',
        }}>
          Dernière mise à jour : 20 avril 2026
        </p>

        <div style={{ fontFamily: F.sans, lineHeight: 1.8, color: C.textSecondary }}>

          {/* Article 1 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              1. Objet
            </h2>
            <p>
              Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'accès et l'utilisation de la plateforme IAvocat.ai (« Plateforme »), une application web proposant des services d'assistance juridique par intelligence artificielle.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              La Plateforme est exploitée par <strong>MY LEGAL ACT INFORMATION TECHNOLOGY CONSULTANTS - FZCO</strong> (ci-après « My Legal Act »), Free Zone Company de droit émirien, immatriculée sous licence n° 66207 délivrée le 11 juillet 2025 par la Dubai Integrated Economic Zones Authority (DIEZ) au sein de l'International Free Zone Authority (IFZA), dont le siège social est situé IFZA Properties, DSO-IFZA, Dubai Silicon Oasis, Dubai, Émirats Arabes Unis.
            </p>
          </section>

          {/* Article 2 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              2. Inscription et Accès
            </h2>
            <p>
              Pour accéder à la Plateforme, l'utilisateur doit être un avocat inscrit au barreau français. L'inscription impose les obligations suivantes :
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.8rem' }}>
              <li>Fournir des informations exactes et complètes (nom, email, numéro de barreau)</li>
              <li>Mettre à jour régulièrement ses données</li>
              <li>Maintenir la confidentialité de ses identifiants d'accès</li>
              <li>Ne pas partager son compte avec des tiers</li>
            </ul>
            <p style={{ marginTop: '0.8rem' }}>
              L'accès à la Plateforme s'effectue via un système d'authentification sécurisée (email + mot de passe ou fédération OAuth). L'utilisateur est responsable de la confidentialité de ses identifiants.
            </p>
          </section>

          {/* Article 3 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              3. Description du Service
            </h2>
            <p>
              IAvocat.ai propose une suite intégrée de services d'assistance juridique :
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.8rem' }}>
              <li><strong>Chat IA juridique :</strong> assistance en temps réel basée sur une intelligence artificielle de pointe</li>
              <li><strong>Gestion de dossiers :</strong> organisation et archivage des dossiers clients</li>
              <li><strong>Agenda intelligent :</strong> synchronisation et gestion des rendez-vous</li>
              <li><strong>Emails centralisés :</strong> agrégation et gestion de la correspondance</li>
              <li><strong>Génération de documents :</strong> rédaction assistée d'actes juridiques</li>
            </ul>
            <p style={{ marginTop: '0.8rem' }}>
              La Plateforme fonctionne selon un modèle d'abonnement mensuel. L'utilisateur bénéficie d'une période d'essai gratuite de quatorze (14) jours à compter de son inscription. Les conditions commerciales de l'abonnement (formules, prix, paiement, reconduction, résiliation) sont régies par les <Link to="/cgv" style={{ color: C.accent, textDecoration: 'underline' }}>Conditions Générales de Vente</Link>.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              L'enregistrement d'un moyen de paiement valide (carte bancaire) est obligatoire à l'inscription. Aucun montant n'est prélevé pendant la période d'essai. À l'issue de cette période, et en l'absence de résiliation par l'utilisateur avant le huitième (8<sup>e</sup>) jour, le plan d'abonnement choisi est automatiquement activé et prélevé selon la périodicité mensuelle, conformément à l'article L. 121-21-2 du Code de la consommation transposant la Directive (UE) 2019/2161 (« Directive Omnibus »). L'utilisateur reçoit un rappel par email au plus tard deux (2) jours avant la fin de la période d'essai.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              L'utilisateur peut résilier à tout moment pendant la période d'essai via les paramètres de son compte. La résiliation prend effet immédiatement et aucun montant ne sera prélevé.
            </p>
          </section>

          {/* Article 4 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              4. Obligations de l'Utilisateur
            </h2>
            <p>
              L'utilisateur s'engage à :
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.8rem' }}>
              <li>Utiliser la Plateforme conformément à la réglementation applicable, notamment le Code de l'organisation judiciaire et le Règlement Intérieur National des barreaux</li>
              <li>Respecter le secret professionnel et la confidentialité des données clients</li>
              <li>Ne pas utiliser la Plateforme à des fins illégales ou contraires à l'ordre public</li>
              <li>Ne pas contourner les mesures de sécurité</li>
              <li>Ne pas télécharger ou extraire les données de la Plateforme de manière non autorisée</li>
              <li>Rester responsable de tous les actes effectués sous son compte</li>
            </ul>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>Responsabilité de l'utilisateur :</strong> L'utilisateur demeure seul responsable des actes juridiques, conseils et documents générés via la Plateforme. L'IA assiste l'avocat mais ne le remplace pas. L'avocat doit exercer son jugement professionnel et vérifier tous les éléments avant de conseiller un client ou produire un acte.
            </p>
          </section>

          {/* Article 5 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              5. Propriété Intellectuelle
            </h2>
            <p>
              L'utilisateur conserve la propriété de ses données et dossiers clients. My Legal Act conserve la propriété de la Plateforme, de ses fonctionnalités, du code source et des contenus fournis (modèles, articles, etc.).
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              L'utilisateur obtient une licence limitée, non exclusive et révocable d'utilisation de la Plateforme selon ses conditions d'abonnement.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              Les résultats générés par l'IA (textes, documents) sont fournis à l'utilisateur à titre de base de travail. L'utilisateur en obtient la propriété à condition de les avoir commandés et payés selon le forfait souscrit.
            </p>
          </section>

          {/* Article 6 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              6. Responsabilité et Garanties
            </h2>
            <p>
              <strong>6.1 Limitation de responsabilité</strong>
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              IAvocat.ai fournit la Plateforme « en l'état ». My Legal Act ne garantit pas :
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.8rem' }}>
              <li>L'exactitude, la complétude ou l'adéquation des résultats IA à la situation spécifique de l'utilisateur</li>
              <li>L'absence d'erreur ou d'omission</li>
              <li>La disponibilité ininterrompue de la Plateforme</li>
              <li>L'absence de virus ou de code malveillant</li>
            </ul>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>6.2 Exonération de responsabilité</strong>
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              My Legal Act ne sera pas responsable des pertes découlant de :
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.8rem' }}>
              <li>L'utilisation ou l'incapacité à utiliser la Plateforme</li>
              <li>Les erreurs ou imprécisions des résultats IA</li>
              <li>Les décisions ou actions prises en se fondant sur les suggestions IA</li>
              <li>Les interruptions d'accès dues à la maintenance ou à des problèmes techniques</li>
              <li>Les préjudices indirects, consécutifs ou punitifs</li>
            </ul>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>6.3 L'utilisateur demeure seul responsable</strong> du respect de ses obligations professionnelles et légales, notamment la confidentialité, la lutte contre le blanchiment de capitaux et l'aide à la personne.
            </p>
          </section>

          {/* Article 7 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              7. Données Personnelles
            </h2>
            <p>
              Le traitement des données personnelles est décrit dans la <Link to="/confidentialite" style={{ color: C.accent, textDecoration: 'underline' }}>Politique de Confidentialité</Link>.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              En résumé, vos données sont :
            </p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.8rem' }}>
              <li>Hébergées dans l'Union européenne (Supabase, AWS eu-west-1, Irlande)</li>
              <li>Chiffrées en transit (TLS 1.3) et au repos</li>
              <li>Non utilisées pour entraîner les modèles IA</li>
              <li>Traitées conformément au RGPD</li>
              <li>Sujettes aux droits d'accès, rectification et suppression</li>
            </ul>
          </section>

          {/* Article 8 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              8. Durée et Résiliation
            </h2>
            <p>
              <strong>8.1 Essai gratuit</strong>
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              Chaque nouvel utilisateur bénéficie de 14 jours d'accès gratuit à la Plateforme. À l'issue de la période d'essai, un abonnement payant doit être souscrit pour continuer l'utilisation.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>8.2 Abonnement</strong>
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              L'abonnement est renouvelé chaque mois à titre automatique selon le plan choisi (249€, 549€ ou 1 190€ HT/mois). La facturation s'effectue via Stripe. Les conditions détaillées (TVA, révision des prix, défaut de paiement) figurent dans les <Link to="/cgv" style={{ color: C.accent, textDecoration: 'underline' }}>Conditions Générales de Vente</Link>.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              Les prix sont exprimés hors taxes. La TVA est autoliquidée par le preneur établi dans un État membre de l'Union européenne, en application des articles 44 et 196 de la Directive 2006/112/CE (transposés aux articles 259 B et 283-2 du Code général des impôts français). Les preneurs non assujettis à la TVA ou établis hors de l'UE sont invités à contacter l'Éditeur pour les modalités de facturation applicables.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              Le premier prélèvement intervient à l'issue de la période d'essai de quatorze jours, sauf résiliation préalable de l'utilisateur. Les prélèvements suivants sont effectués mensuellement à date anniversaire.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>8.3 Résiliation</strong>
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              L'utilisateur peut résilier à tout moment sans préavis via les paramètres de son compte. La résiliation prend effet immédiatement. Aucun remboursement n'est accordé pour les mois partiellement utilisés.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              My Legal Act peut suspendre ou résilier l'accès en cas de violation des CGU.
            </p>
          </section>

          {/* Article 9 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              9. Droit Applicable et Litiges
            </h2>
            <p>
              Les présentes CGU sont régies par le droit français.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>9.1 Règlement amiable.</strong> Avant toute action judiciaire, les parties s'engagent à tenter de résoudre à l'amiable tout différend né de l'exécution ou de l'interprétation des présentes. La partie la plus diligente adresse à l'autre une réclamation motivée par courrier électronique à l'adresse contact@iavocat.ai. Les parties disposent d'un délai de trente (30) jours à compter de la réception de cette réclamation pour parvenir à un accord.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>9.2 Juridiction.</strong> À défaut d'accord amiable, la juridiction compétente sera déterminée selon les règles applicables, notamment le Règlement (UE) 1215/2012 du 12 décembre 2012 (« Bruxelles I bis ») pour les utilisateurs établis dans l'Union européenne, et les règles de droit international privé applicables pour les autres.
            </p>
            <p style={{ marginTop: '0.8rem' }}>
              <strong>9.3 Données personnelles.</strong> Les litiges portant exclusivement sur le traitement de données personnelles peuvent être portés devant l'autorité de contrôle compétente (en France : la CNIL) ou devant la juridiction compétente selon l'article 79 du Règlement (UE) 2016/679.
            </p>
          </section>

          {/* Article 10 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 500,
              color: C.textPrimary,
              marginBottom: '1rem',
            }}>
              10. Modifications des CGU
            </h2>
            <p>
              My Legal Act se réserve le droit de modifier les CGU à tout moment. Les modifications seront communiquées à l'utilisateur au moins 15 jours avant leur entrée en vigueur. Poursuivre l'utilisation après cette date implique l'acceptation des modifications.
            </p>
          </section>

          <hr style={{ margin: '2.5rem 0', border: 'none', borderTop: `0.5px solid ${C.border}` }} />

          <p style={{ fontSize: '0.9rem', color: C.textSecondary, marginTop: '2rem' }}>
            Pour toute question sur ces CGU, contactez-nous à contact@iavocat.ai.
          </p>
        </div>
      </div>
    </div>
  )
}
